<template>
    <div class="o-changePassword u-w100 u-py20 u-overflowYAuto">
        <div class="o-changePassword__inner u-container u-overflowHidden">
            <ul>
                <li>{{ $t('profile.passwordMinLength') }}</li>
                <li>{{ $t('profile.passwordLogin') }}</li>
            </ul>
            <form @submit.prevent="changePassword">
                <div>
                    <label class="sr-only" for="current_password">{{ $t('profile.currentPassword') }}</label>
                    <input
                        type="password"
                        v-model="currentPassword"
                        id="current_password"
                        required
                        :disabled="isLoading"
                        :placeholder="$t('profile.currentPassword')"
                    />
                </div>
                <div class="u-mt20">
                    <label class="sr-only" for="new_password">{{ $t('profile.newPassword') }}</label>
                    <input
                        type="password"
                        v-model="newPassword"
                        id="new_password"
                        required
                        :disabled="isLoading"
                        :placeholder="$t('profile.newPassword')"
                    />
                </div>
                <div class="u-mt20">
                    <label class="sr-only" for="confirm_password">{{ $t('profile.confirmPassword') }}</label>
                    <input
                        type="password"
                        v-model="confirmPassword"
                        id="confirm_password"
                        required
                        :disabled="isLoading"
                        :placeholder="$t('profile.confirmPassword')"
                    />
                </div>
                <div class="o-changePassword__cta u-mt20">
                    <button 
                        :class="{ '-loading': isLoading }" 
                        class="a-button" 
                        type="submit" 
                        :disabled="isLoading">
                        {{ $t('profile.changePassword') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
    <Notice
        class="-smallPaddingBottom"
        :title="notice.info.title" 
        :message="notice.info.message" 
        :state="notice.info.state" 
        :isVisible="notice.isVisible"
    />
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '../services/api';

// Components
import Notice from '../components/molecules/Notice.vue';

const { t } = useI18n();

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const isLoading = ref(false);
const noticeTimeout = ref(null);

const emit = defineEmits(['updateHeader']);

const notice = ref({
    info: {
        title: '',
        message: '',
        state: ''
    },
    isVisible: false
});

const showNotice = (title, message, state) => {
    if (noticeTimeout.value) {
        clearTimeout(noticeTimeout.value);
    }

    notice.value.info.title = title;
    notice.value.info.message = message;
    notice.value.info.state = state;
    notice.value.isVisible = true;

    noticeTimeout.value = setTimeout(hideNotice, 4000);
};


const hideNotice = () => {
    notice.value.isVisible = false;

    if (noticeTimeout.value) {
        clearTimeout(noticeTimeout.value);
        noticeTimeout.value = null;
    }
};

const changePassword = async () => {
    if (newPassword.value !== confirmPassword.value) {
        showNotice(t('general.somethingWentWrong'), t('profile.passwordMismatch'), '-error');
        return;
    }

    isLoading.value = true;
    try {
        const response = await api.post('/change-password', {
            current_password: currentPassword.value,
            new_password: newPassword.value,
            confirm_password: confirmPassword.value,
        });

        if (response.status === 200) {
            showNotice(t('general.success'), t('profile.passwordChangeSuccess'), '-success');

            setTimeout(() => {
                window.location.href = '/login';
            }, 3000);

        } else {
            showNotice(t('general.somethingWentWrong'), t('profile.passwordChangeError'), '-error');
        }
    } catch (err) {
        if (err.response?.status === 422) {
            const tempMessage = t('profile.passwordRequirementsError') || err.response?.data?.error;
            showNotice(t('general.somethingWentWrong'), tempMessage, '-error');
        } else if (err.response?.status === 401) {
            showNotice(t('general.somethingWentWrong'), t('profile.invalidCurrentPassword'), '-error');
        } else {
            console.error('Error changing password:', err);
            showNotice(t('general.somethingWentWrong'), t('profile.passwordChangeError'), '-error');
        }
    } finally {
        isLoading.value = false;
    }
};

</script>